import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import TeamQRCodeColorPicker from 'team/components/editMember/qrCode/TeamQRCodeColorPicker';
import TeamQrCodeCustomLogo from 'team/components/editMember/qrCode/TeamQrCodeCustomLogo';
import { useQRCodeContext } from 'contexts/QrCodeContext';
import { useAuth } from 'contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';

const TeamTemplateQRCodeContainer = ({ template, onTemplateUpdate, isCreating = false, onSave, onCancel, isSubmitting = false }) => {
    const qrCode = template?.qrCode || {};
    const { authState } = useAuth();
    const location = useLocation();
    const { qrCodeState, setQRCodeColor, setQRCodeLogoUrl } = useQRCodeContext();
    
    // Detect template context and get user's cardId for QR code context
    const isTemplateContext = location.pathname.includes('/template/');
    const userCardId = authState.user?.activeCardId || 
                      (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);
    
    // Store initial QR code values to detect changes
    const initialQrCodeRef = useRef(null);
    const [hasChanges, setHasChanges] = useState(false);

    // Initialize initial values when template loads
    useEffect(() => {
        if (template?.qrCode) {
            const initialColor = isTemplateContext && userCardId && qrCodeState[userCardId]?.contextQrCodeColor
                ? qrCodeState[userCardId].contextQrCodeColor
                : (template.qrCode.color || '#000000');
            const initialLogoUrl = isTemplateContext && userCardId && qrCodeState[userCardId]?.contextQrCodeLogoUrl
                ? qrCodeState[userCardId].contextQrCodeLogoUrl
                : (template.qrCode.logoUrl || null);
            
            initialQrCodeRef.current = {
                color: initialColor,
                logoUrl: initialLogoUrl
            };
            setHasChanges(false); // Reset changes when template loads
        }
    }, [template?._id]); // Reset when template ID changes

    // Sync QR code context changes to template (only when context changes, not on every render)
    useEffect(() => {
        if (!isTemplateContext || !userCardId || !qrCodeState[userCardId]) return;

        const contextColor = qrCodeState[userCardId]?.contextQrCodeColor;
        const contextLogoUrl = qrCodeState[userCardId]?.contextQrCodeLogoUrl;

        // Only update template if context values differ from template values
        if (contextColor && contextColor !== (qrCode.color || '#000000')) {
            onTemplateUpdate('qrCode', {
                ...qrCode,
                color: contextColor
            });
        }
        if (contextLogoUrl !== undefined && contextLogoUrl !== (qrCode.logoUrl || null)) {
            onTemplateUpdate('qrCode', {
                ...qrCode,
                logoUrl: contextLogoUrl
            });
        }
    }, [qrCodeState[userCardId]?.contextQrCodeColor, qrCodeState[userCardId]?.contextQrCodeLogoUrl]); // Only sync when context values change

    // Check for changes by comparing current values with initial values
    useEffect(() => {
        if (!initialQrCodeRef.current) return;

        const currentColor = isTemplateContext && userCardId && qrCodeState[userCardId]?.contextQrCodeColor
            ? qrCodeState[userCardId].contextQrCodeColor
            : (qrCode.color || '#000000');
        const currentLogoUrl = isTemplateContext && userCardId && qrCodeState[userCardId]?.contextQrCodeLogoUrl !== undefined
            ? qrCodeState[userCardId].contextQrCodeLogoUrl
            : (qrCode.logoUrl || null);

        const hasColorChange = currentColor !== initialQrCodeRef.current.color;
        const hasLogoChange = currentLogoUrl !== initialQrCodeRef.current.logoUrl;

        setHasChanges(hasColorChange || hasLogoChange);
    }, [qrCode, qrCodeState, userCardId, isTemplateContext]);

    const handleChange = (field, value) => {
        onTemplateUpdate('qrCode', {
            ...qrCode,
            [field]: value
        });
    };

    const handleCancel = () => {
        // Reset to initial values
        if (initialQrCodeRef.current) {
            onTemplateUpdate('qrCode', {
                ...qrCode,
                color: initialQrCodeRef.current.color,
                logoUrl: initialQrCodeRef.current.logoUrl
            });
            
            // Also reset QR code context if in template context
            if (isTemplateContext && userCardId) {
                setQRCodeColor(userCardId, initialQrCodeRef.current.color);
                if (initialQrCodeRef.current.logoUrl) {
                    setQRCodeLogoUrl(userCardId, initialQrCodeRef.current.logoUrl);
                } else {
                    setQRCodeLogoUrl(userCardId, null);
                }
            }
        }
        if (onCancel) {
            onCancel();
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: 2,
                backgroundColor: '#ffffff'
            }}
        >
            {/* Header Section */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    marginBottom: 2,
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px' }} gutterBottom align="left">
                    QR Code
                </Typography>
            </Box>

            {/* Content Section */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 2,
                    borderRadius: 1,
                }}
            >
                {/* QR Code Color Picker */}
                <Box sx={{ marginTop: 2 }}>
                    <TeamQRCodeColorPicker
                        cardId={isTemplateContext ? userCardId : undefined}
                        isColorRestricted={false}
                        canRequestChange={false}
                        onRestrictionNotification={() => { }}
                    />
                </Box>

                {/* QR Code Custom Logo */}
                <Box sx={{ marginTop: 4 }}>
                    <TeamQrCodeCustomLogo
                        cardId={isTemplateContext ? userCardId : undefined}
                        isLogoRestricted={false}
                        canRequestChange={false}
                        onRestrictionNotification={() => { }}
                    />
                </Box>

                {/* Cancel and Update buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, marginTop: 4 }}>
                    <TeamWhiteButtonWithIcon
                        text="Cancel"
                        onClick={handleCancel}
                        disabled={isSubmitting || !hasChanges}
                        padding="5px 16px"
                        sx={{
                            border: '0.75px solid #dedede',
                        borderRadius: '10px',
                            fontSize: '0.8rem',
                        marginRight: "4px",
                            color: !hasChanges ? '#a6a6a6' : '#212121',
                        '&:hover': {
                                backgroundColor: !hasChanges ? '#ffffff' : '#f7f7f7',
                                border: '0.75px solid #dedede',
                                boxShadow: 'none',
                        },
                            '&.Mui-disabled': {
                                border: '0.75px solid #e0e0e0',
                                color: '#a6a6a6'
                            }
                    }}
                    />
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={onSave}
                        disabled={isSubmitting || !hasChanges}
                    sx={{
                            backgroundColor: !hasChanges ? '#e0e0e0' : '#000000',
                        padding: '5px 32px',
                            color: !hasChanges ? '#a6a6a6' : '#ffffff',
                        fontWeight: "bold",
                        textTransform: 'none',
                        borderRadius: '10px',
                        '&:hover': {
                                backgroundColor: !hasChanges ? '#e0e0e0' : '#333333',
                        },
                            '&.Mui-disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#a6a6a6'
                        }
                    }}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (isCreating ? 'Save' : 'Update')}
                </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default TeamTemplateQRCodeContainer;

